import {
  type FieldApi,
  FieldRow,
  handleStringChange,
} from '@/components/form/formHelpers'
import {
  ORGANIZATION_NAME_MAX_LENGTH,
  ORGANIZATION_NAME_PATTERN,
  invalidateRenamedOrganization,
  renameErrorMessageKey,
} from '@/features/Organizations/lib/renameOrganization'
import { useRenameOrganisation } from '@/generated/http-clients/v3ApiComponents'
import {
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
} from '@/hooks/useToast/toastUtils'
import {
  Alert,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Input,
  Portal,
  VStack,
} from '@chakra-ui/react'
import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const KEY_PREFIX = 'orgDetails.rename'

type Props = {
  orgId: string
  currentName: string
  onClose: () => void
}

export function RenameOrganizationDialog({
  orgId,
  currentName,
  onClose,
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()

  const mutation = useRenameOrganisation({
    onSuccess: () => {
      invalidateRenamedOrganization(queryClient, orgId)
      showOrUpdateToast('rename-organization', {
        ...SUCCESS_TOAST_OPTION,
        title: String(t(`${KEY_PREFIX}.success`)),
      })
      onClose()
    },
  })

  const schema = z.object({
    name: z
      .string()
      .trim()
      .min(1, String(t(`${KEY_PREFIX}.validation.required`)))
      .max(
        ORGANIZATION_NAME_MAX_LENGTH,
        String(t(`${KEY_PREFIX}.validation.maxLength`)),
      )
      .regex(
        ORGANIZATION_NAME_PATTERN,
        String(t(`${KEY_PREFIX}.validation.pattern`)),
      ),
  })

  const form = useForm({
    defaultValues: { name: currentName },
    validators: { onChange: schema },
    onSubmit: ({ value }) => {
      mutation.mutate({
        pathParams: { orgId },
        body: { name: value.name.trim() },
      })
    },
  })

  return (
    <Dialog.Root
      open
      onOpenChange={(details) => {
        if (!details.open) {
          onClose()
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            as="form"
            onSubmit={(event) => {
              event.preventDefault()
              event.stopPropagation()
              void form.handleSubmit()
            }}
          >
            <Dialog.Header pb={3}>
              <Dialog.Title>{t(`${KEY_PREFIX}.title`)}</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={4}>
                {mutation.isError ? (
                  <Alert.Root status="error" role="alert">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>
                        {t(`${KEY_PREFIX}.error.title`)}
                      </Alert.Title>
                      <Alert.Description>
                        {t(renameErrorMessageKey(mutation.error))}
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                ) : null}
                <form.Field name="name">
                  {(field) => (
                    <FieldRow
                      field={field as FieldApi<unknown>}
                      label={String(t(`${KEY_PREFIX}.fields.name.label`))}
                      helperText={String(t(`${KEY_PREFIX}.fields.name.helper`))}
                    >
                      <Input
                        value={field.state.value}
                        onChange={handleStringChange(field)}
                        onBlur={field.handleBlur}
                      />
                    </FieldRow>
                  )}
                </form.Field>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={2}>
                <Button variant="outline" onClick={onClose}>
                  {t(`${KEY_PREFIX}.cancel`)}
                </Button>
                <form.Subscribe
                  selector={(state) => ({
                    canSubmit: state.canSubmit,
                    isUnchanged: state.values.name.trim() === currentName,
                  })}
                >
                  {({ canSubmit, isUnchanged }) => (
                    <Button
                      type="submit"
                      colorPalette="brand"
                      loading={mutation.isPending}
                      disabled={!canSubmit || isUnchanged || mutation.isPending}
                    >
                      {t(`${KEY_PREFIX}.submit`)}
                    </Button>
                  )}
                </form.Subscribe>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
